import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "../components/Layout";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";
import { COST_RECIPES, computeSqInchArea, recipesForPart, autofillInputs, RM_TYPES, CUTTING_TYPES_CS } from "../utils/costRecipes";
import { ArrowLeft, FloppyDisk, Printer, FileXls, FilePdf, Plus, Trash, CaretDown, CaretRight, DotsSixVertical } from "@phosphor-icons/react";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import SearchableSelect from "../components/SearchableSelect";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const rupee = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };

// Auto-fill dimensional inputs for the RM section from the part row (BOM
// Routing writes these onto Part Master). Values remain editable in the UI.
function dimensionAutofill(rmType, part) {
  const t = String(rmType || "").toLowerCase();
  if (t === "rod") return { od: part.diameter ?? part.outer_diameter ?? "", length: part.length ?? "" };
  if (t === "pipe") return { od: part.outer_diameter ?? "", id: part.inner_diameter ?? "", length: part.length ?? "" };
  if (t === "sheet" || t === "strip") return { width: part.width ?? "", length: part.length ?? "", thickness: part.thickness ?? "" };
  return {};
}

// Cost Sheet business category badge — resolves the category for a given
// recipe key. Preference order: Process Master row → built-in defaults.
const CATEGORY_META = {
  fabrication:       { label: "Fabrication",       tone: "bg-[#EEF2FF] text-[#4338CA] border-[#C7D2FE]" },
  surface_treatment: { label: "Surface Treatment", tone: "bg-[#FEF3C7] text-[#B45309] border-[#FCD34D]" },
  surface_finish:    { label: "Surface Finish",    tone: "bg-[#DCFCE7] text-[#166534] border-[#86EFAC]" },
  none:              { label: "None",              tone: "bg-[#F1F5F9] text-[#64748B] border-[#CBD5E1]" },
};
// Fallback badge appearance for custom (user-added) categories.
const CUSTOM_CATEGORY_TONE = "bg-[#F5F3FF] text-[#6D28D9] border-[#DDD6FE]";
const BUILTIN_CATEGORY = {
  raw_material: "fabrication",
  cutting: "fabrication",
  machining: "fabrication",
  deburring: "fabrication",
  packaging: "fabrication",
  surface_treatment: "surface_treatment",
  surface_finish: "surface_finish",
};
function resolveCategory(key, processes) {
  const raw = String(key || "").toLowerCase();
  // Strip the __N variant suffix so "machining__2" still resolves to the
  // same category as the first machining section.
  const kl = raw.replace(/__\d+$/, "");
  // First look for a Process Master row (either exact code match or the
  // machining_p1/p2 aliases mapping to canonical "machining").
  const row = (processes || []).find(p => {
    const c = (p.code || "").toLowerCase();
    if (c === kl) return true;
    if (kl === "machining" && c.startsWith("machining")) return true;
    return false;
  });
  if (row?.process_category) return row.process_category;
  return BUILTIN_CATEGORY[kl] || "fabrication";
}

const PIE_COLORS = ["#1D2951", "#F5B301", "#0F766E", "#7C3AED", "#DC2626", "#059669",
                    "#EA580C", "#0891B2", "#DB2777", "#65A30D", "#4C1D95", "#B45309"];

// Profit tone thresholds (green > 25%, yellow 10–25%, red < 10%)
const profitTone = (pct) => {
  // User-specified bands (Feb 2026):
  //   pct < 0         → High Risk (deep red)
  //   0   ≤ pct < 5   → Risk       (red)
  //   5   ≤ pct < 10  → Okay       (amber)
  //   10  ≤ pct < 15  → Fair       (yellow)
  //   15  ≤ pct < 20  → Good       (light green)
  //   pct ≥ 20        → Healthy    (deep green)
  if (pct < 0)  return { bg: "#FEE2E2", border: "#FCA5A5", fg: "#7F1D1D", label: "High Risk" };
  if (pct < 5)  return { bg: "#FEE2E2", border: "#FCA5A5", fg: "#991B1B", label: "Risk" };
  if (pct < 10) return { bg: "#FFEDD5", border: "#FED7AA", fg: "#9A3412", label: "Okay" };
  if (pct < 15) return { bg: "#FEF9C3", border: "#FDE047", fg: "#854D0E", label: "Fair" };
  if (pct < 20) return { bg: "#DCFCE7", border: "#BBF7D0", fg: "#166534", label: "Good" };
  return          { bg: "#D1FAE5", border: "#86EFAC", fg: "#064E3B", label: "Healthy" };
};

export default function CostSheetEditor() {
  const { id } = useParams();
  const isNew = id === "new";
  const navigate = useNavigate();

  const [parts, setParts] = useState([]);
  const [bomMaster, setBomMaster] = useState([]);   // full material catalogue
  const [processes, setProcesses] = useState([]);   // for cutting-process rates
  const [machiningConfig, setMachiningConfig] = useState(null); // CNC/VMC slabs + deburring MHR
  const [selectedPart, setSelectedPart] = useState(null);
  const [sellingPrice, setSellingPrice] = useState(0);
  const [notes, setNotes] = useState("");
  // `lines` = { [recipeKey]: { enabled?, ...inputs } }
  const [lines, setLines] = useState({});
  const [expanded, setExpanded] = useState({});
  const [saving, setSaving] = useState(false);
  // User-controlled list of processes shown on this cost sheet — replaces
  // the older "show every enabled op automatically" flow. RM is pre-added
  // on part-pick as a convenience but the user can remove/reorder freely.
  const [addedProcessKeys, setAddedProcessKeys] = useState([]);
  const printRef = useRef(null);

  // ── Load parts (dropdown source) + master data for auto-fetches ─────
  useEffect(() => {
    axios.get(`${API}/parts`)
      .then(r => setParts(r.data || []))
      .catch(() => toast.error("Failed to load parts"));
    axios.get(`${API}/bom-master`).then(r => setBomMaster(r.data || [])).catch(() => {});
    axios.get(`${API}/processes`).then(r => setProcesses(r.data || [])).catch(() => {});
    axios.get(`${API}/settings/machining-config`).then(r => setMachiningConfig(r.data || null)).catch(() => {});
  }, []);

  // ── Load existing sheet (edit mode) ──────────────────────────────
  useEffect(() => {
    if (isNew) return;
    axios.get(`${API}/cost-sheets/${id}`)
      .then(r => {
        const d = r.data || {};
        setSellingPrice(d.selling_price || 0);
        setLines(d.lines || {});
        setNotes(d.notes || "");
        setAddedProcessKeys(Array.isArray(d.added_processes) ? d.added_processes : []);
        // Set selected part when parts list arrives
        const findAndSet = (list) => {
          const p = list.find(x => x.id === d.part_id);
          if (p) setSelectedPart(p);
        };
        if (parts.length) findAndSet(parts);
        else axios.get(`${API}/parts`).then(pr => { setParts(pr.data || []); findAndSet(pr.data || []); });
      })
      .catch(() => toast.error("Cost sheet not found"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew]);

  // ── Recipe list — driven entirely by the user's "+ Add Process" picks.
  // We look up a matching recipe from the library (by key or aliases) for
  // each added process; fall back to the generic recipe with the process
  // label so any custom process still gets a costable section.
  const recipes = useMemo(() => {
    if (!selectedPart) return [];
    return addedProcessKeys.map((k) => {
      const kl = String(k).toLowerCase();
      // Strip the __N variant suffix — same base recipe, unique storage key.
      const suffixMatch = kl.match(/__(\d+)$/);
      const base = kl.replace(/__\d+$/, "");
      // Merge machining_p1/p2 into a single machining section.
      const canonical = base.startsWith("machining") ? "machining" : base;
      // Also inspect the Process Master name so a custom process code
      // like "deb_1" but named "Deburring" still resolves to the right
      // recipe (defends against mis-coded processes on production).
      const procMeta = processes.find(p => (p.code || "").toLowerCase() === base);
      const procName = (procMeta?.name || "").toLowerCase().replace(/\s+/g, "_");

      let recipe = COST_RECIPES.find(r => r.key === canonical);
      if (!recipe) {
        recipe = COST_RECIPES.find(r =>
          r.aliases?.includes(canonical) ||
          r.aliases?.some(a => canonical.includes(a) || a.includes(canonical)) ||
          (procName && (r.aliases?.some(a => procName.includes(a) || a.includes(procName)) || r.key === procName))
        );
      }
      if (!recipe) {
        // Custom process (from Process Master) — default to Rate × Weight.
        const label = procMeta?.name || base.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
        recipe = { ...COST_RECIPES.find(r => r.key === "rate_x_weight"), label };
      }
      // Tag the label for duplicate variants ("Machining #2") and always
      // set the recipe key to the unique storage key so all state (lines,
      // expanded, remove) routes to the right slot.
      const label = suffixMatch ? `${recipe.label} #${suffixMatch[1]}` : recipe.label;
      return { ...recipe, key: kl, label };
    });
  }, [selectedPart, addedProcessKeys, processes]);

  // Full list of processes available to add — sourced from Process Master.
  // Excludes system-only ops that don't carry a real cost. Every process
  // (added or not) stays visible in the dropdown so the user always sees
  // the full menu; re-picking one already on the sheet is a no-op.
  const addableProcesses = useMemo(() => {
    const HIDE = new Set(["incoming_store", "outsource", "outsource_cutting", "outsource_machining", "firewall", "semi_firewall", "dispatch"]);
    const rows = [{ key: "raw_material", label: "Raw Material" }];
    const seen = new Set(["raw_material"]);
    (processes || []).forEach(p => {
      const code = (p.code || "").toLowerCase();
      if (!code || HIDE.has(code)) return;
      // Machining P1 / P2 both share one "machining" recipe — collapse into a single option.
      const key = code.startsWith("machining") ? "machining" : code;
      if (seen.has(key)) return;
      seen.add(key);
      const label = code.startsWith("machining") ? "Machining" : (p.name || code);
      rows.push({ key, label });
    });
    // Also expose built-in recipes that may not be present in Process Master
    // (deburring, surface_treatment, packaging, machining, etc.) so the user
    // can always pick them regardless of what's in the Process Master.
    COST_RECIPES.forEach(rec => {
      const k = rec.key;
      if (seen.has(k) || k === "generic") return;
      seen.add(k);
      rows.push({ key: k, label: rec.label });
    });
    return rows;
  }, [processes]);

  const addProcess = (key) => {
    if (!key) return;
    let base = String(key).toLowerCase();
    // Canonicalize machining_p1 / machining_p2 → single "machining" recipe.
    if (base.startsWith("machining")) base = "machining";
    // If this base already exists on the sheet, append a variant suffix so
    // the user can add the same process again with independent state (e.g.
    // one Machining section for CNC and a second for VMC).
    let finalKey = base;
    if (addedProcessKeys.includes(finalKey)) {
      let n = 2;
      while (addedProcessKeys.includes(`${base}__${n}`)) n++;
      finalKey = `${base}__${n}`;
    }
    setAddedProcessKeys(prev => [...prev, finalKey]);
    setExpanded(exp => ({ ...exp, [finalKey]: true }));
    // Auto-seed sensible defaults + BOM-Routing autofills for the newly
    // added process. Weight-based recipes (packaging, dispatch, surface_*)
    // pull `weight` from the selected part's `finished_weight`. Deburring
    // seeds MHR from tenant config (fallback ₹180/hr) so the field is
    // never blank at zero.
    const rec = COST_RECIPES.find(r => r.key === base);
    const seed = {};
    if (rec?.optional) seed.enabled = true;
    if (selectedPart) {
      // autoFrom-style seeding for flat `inputs` recipes
      const fw = num(selectedPart.finished_weight ?? selectedPart.finish_weight ?? selectedPart.net_weight ?? 0);
      if (Array.isArray(rec?.inputs)) {
        rec.inputs.forEach(f => {
          if (f.autoFrom && selectedPart[f.autoFrom] != null && selectedPart[f.autoFrom] !== "") {
            seed[f.key] = selectedPart[f.autoFrom];
          }
        });
      }
      // Deburring — no `inputs`, but seed MHR + defaults so user only needs
      // to type Output/Efficiency (efficiency defaults to 75%).
      if (base === "deburring") {
        seed.mhr = machiningConfig?.deburring_mhr ?? 180;
        seed.efficiency = 75;
      }
      // Ensure packaging/dispatch weight lands even if `autoFrom` wasn't
      // resolved (safety belt for legacy part docs).
      if ((base === "packaging" || base === "dispatch") && fw > 0 && !seed.weight) {
        seed.weight = fw;
      }
    }
    if (Object.keys(seed).length) {
      setLines(prev => ({ ...prev, [finalKey]: { ...seed, ...(prev[finalKey] || {}) } }));
    }
  };
  const removeProcess = (key) => {
    const kl = String(key).toLowerCase();
    setAddedProcessKeys(prev => prev.filter(k => k !== kl));
  };

  // ── Drag-and-drop reordering of the process sections ─────────────
  // The order in `addedProcessKeys` is the source of truth for both the
  // rendered sequence and what gets saved into `added_processes`. Raw
  // Material is pinned to the top and cannot be reordered.
  const [dragKey, setDragKey] = useState(null);
  const reorderProcess = (fromKey, toKey) => {
    if (!fromKey || !toKey || fromKey === toKey) return;
    if (fromKey === "raw_material" || toKey === "raw_material") return;
    setAddedProcessKeys(prev => {
      const from = prev.indexOf(fromKey);
      const to   = prev.indexOf(toKey);
      if (from < 0 || to < 0) return prev;
      const next = prev.slice();
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
    toast.success("Sequence updated — remember to Save.", { id: "cs-reorder" });
  };

  // When user picks a part, prefill selling price + autofill inputs.
  const onPickPart = async (partId) => {
    const p = parts.find(x => x.id === partId);
    if (!p) { setSelectedPart(null); return; }
    setSelectedPart(p);
    setSellingPrice(p.part_rate || 0);
    // Pre-add Raw Material (user can still remove it).
    setAddedProcessKeys(prev => prev.includes("raw_material") ? prev : ["raw_material", ...prev]);

    // Fetch weighted-avg RM rate for the part's rm_code — pulls the true
    // procurement average from receive history. If none, fall back to
    // Item Master rm_rate and flag it so the user knows to override.
    let avgRateInfo = { has_data: false, avg_rate: 0, sample_count: 0, total_qty_kg: 0 };
    if (p.rm_code) {
      try {
        const r = await axios.get(`${API}/rm/avg-rate`, { params: { rm_code: p.rm_code } });
        avgRateInfo = r.data || avgRateInfo;
      } catch { /* silent — falls back to manual entry */ }
    }

    // Look up BOM Master row for the part's RM grade — gives density,
    // rate & scrap_rate defaults for the RM section.
    const bomRow = bomMaster.find(b => (b.grade || "").toLowerCase() === (p.rm_grade || "").toLowerCase()) || null;

    // Prefill recipe inputs — but don't reset if user already entered values.
    const partSnapshot = {
      ...p,
      sq_inch_area: computeSqInchArea(p),
      // Override rm_rate with the weighted-avg from receives when available
      rm_rate: avgRateInfo.has_data ? avgRateInfo.avg_rate : (p.rm_rate || bomRow?.total || bomRow?.rate || 0),
    };
    setLines(prev => {
      const next = { ...prev };
      // Prime the advanced Raw Material section with grade / type / density
      // / rate / scrap defaults AND auto-fetch dimensional inputs from
      // BOM Routing (`parts.diameter/outer_diameter/inner_diameter/…`).
      // Dimensions from the Part / BOM Routing ALWAYS win — they're the
      // ground truth for what the operator entered on the routing sheet.
      const rmExisting = next.raw_material || {};
      const finishWeight = num(p.finished_weight ?? p.finish_weight ?? p.net_weight ?? 0);
      const rmType = p.rm_type || "Rod";
      const dims = dimensionAutofill(rmType, p);
      next.raw_material = {
        // 1. Legacy user edits (rm_grade override, custom scrap%, etc.)
        ...rmExisting,
        // 2. Fresh values driven off the newly-picked part
        rm_grade: p.rm_grade || "",
        rm_type: rmType,
        density: bomRow?.density ?? "",
        rm_rate: partSnapshot.rm_rate,
        scrap_rate: bomRow?.scrap_rate ?? "",
        scrap_recovery_pct: rmExisting.scrap_recovery_pct ?? 95,
        finish_weight: finishWeight,
        // 3. Dimensions from BOM Routing — these ALWAYS override any prior
        //    values so the freshly-picked part's true dimensions surface.
        ...dims,
        _rm_rate_source: avgRateInfo.has_data
          ? { kind: "avg", rate: avgRateInfo.avg_rate, samples: avgRateInfo.sample_count, qty: avgRateInfo.total_qty_kg }
          : { kind: "none" },
      };
      // Raw material starts enabled by default; other optional sections start off.
      if (!("enabled" in next.raw_material)) next.raw_material.enabled = true;
      return next;
    });
    setExpanded(exp => ({ ...exp, raw_material: true }));
  };

  // ── Compute all line costs on every render (live) ────────────────
  const cuttingProcess = useMemo(
    () => processes.find(p => (p.behaviour || "").toLowerCase() === "cutting") || null,
    [processes]
  );
  const computed = useMemo(() => {
    const out = {};
    let manufacturing_cost = 0;
    // First pass — compute RM so cutting-shearing can read the gross_weight.
    const rmInputs = lines.raw_material || {};
    const rmRecipe2 = recipes.find(r => r.key === "raw_material");
    const rmEnabled = rmRecipe2?.optional ? !!rmInputs.enabled : true;
    const rmCalcBase = (rmRecipe2 && rmEnabled) ? rmRecipe2.compute(rmInputs) : { cost: 0 };
    // Every section carries an optional manual "inspection_cost" that adds
    // on top of the recipe's own formula-derived cost.
    const rmInsp = num(rmInputs.inspection_cost);
    const rmCalc = { ...rmCalcBase, cost: (rmCalcBase.cost || 0) + rmInsp, inspection_cost: rmInsp };
    out.raw_material = rmCalc;
    if (rmEnabled) manufacturing_cost += rmCalc.cost || 0;

    recipes.forEach(r => {
      if (r.key === "raw_material") return;   // already handled
      const inputs = lines[r.key] || {};
      const isOn = r.optional ? !!inputs.enabled : true;
      // Look up the Process Master row for this recipe so isRateXWeight
      // and isSilverPlatingAdvanced can auto-fetch the standardised rate.
      // Strip the __N variant suffix so machining__2 → machining etc.
      const base = r.key.toLowerCase().replace(/__\d+$/, "");
      const canonical = base.startsWith("machining") ? "machining" : base;
      const procMeta = processes.find(p => {
        const c = (p.code || "").toLowerCase();
        return c === base || (canonical === "machining" && c.startsWith("machining"));
      });
      // Every non-RM recipe gets `finished_weight` in its ctx so any
      // weight-based compute can fall back to the part's finished weight
      // when the user hasn't typed one explicitly (packaging, dispatch,
      // anodising / nickel plating / zinc plating / etc.).
      const finishedWeightCtx = num(selectedPart?.finished_weight ?? selectedPart?.finish_weight ?? selectedPart?.net_weight ?? 0);
      const ctx = canonical === "cutting"
        ? { process: cuttingProcess || {}, gross_weight: rmCalc.gross_weight || 0, rmSection: rmInputs, finished_weight: finishedWeightCtx }
        : (canonical === "machining" || canonical === "deburring")
          ? { machiningConfig: machiningConfig || {}, rmSection: rmInputs, finished_weight: finishedWeightCtx }
          : { processMeta: procMeta || {}, finished_weight: finishedWeightCtx };
      const base_calc = isOn ? (r.compute ? r.compute(inputs, ctx) : { cost: 0 }) : { cost: 0 };
      const insp = isOn ? num(inputs.inspection_cost) : 0;
      const calc = { ...base_calc, cost: (base_calc.cost || 0) + insp, inspection_cost: insp };
      out[r.key] = calc;
      manufacturing_cost += calc.cost || 0;
    });
    // Overhead buffers — 1% Insurance/Carrying/Contingency (ICC) and 1%
    // Rejection, both computed off the Gross RM Value (falls back to Net
    // RM Value when Gross isn't present). Both roll into the total so
    // Profit reflects them.
    const rmBasis = num(rmCalc.gross_rm_value ?? rmCalc.cost);
    const icc = rmBasis * 0.01;
    const rejection = rmBasis * 0.01;
    manufacturing_cost += icc + rejection;
    // Process Cost = every non-RM section's rolled-up cost (used for
    // both the Cost Break-up summary row and the Overhead calc).
    const rm_cost = num(out.raw_material?.cost);
    const process_cost = recipes
      .filter(r => r.key !== "raw_material")
      .reduce((sum, r) => sum + num(out[r.key]?.cost), 0);
    // Overhead — flat 20% of the Process Cost (i.e. all non-RM process
    // costs summed together). Does NOT compound on itself.
    const overhead = process_cost * 0.20;
    manufacturing_cost += overhead;
    const profit = num(sellingPrice) - manufacturing_cost;
    const profit_pct = num(sellingPrice) > 0 ? (profit / num(sellingPrice)) * 100 : 0;
    return { ...out, manufacturing_cost, profit, profit_pct, icc, rejection, overhead, process_cost, rm_cost };
  }, [recipes, lines, sellingPrice, cuttingProcess, machiningConfig, processes, selectedPart]);

  // Pie chart data — only include lines with a cost > 0.
  const pieData = useMemo(() => {
    return recipes
      .map(r => ({ name: r.label, value: Math.max(0, computed[r.key]?.cost || 0), key: r.key }))
      .filter(d => d.value > 0);
  }, [recipes, computed]);

  const setLine = (key, patch) => setLines(prev => ({ ...prev, [key]: { ...(prev[key] || {}), ...patch } }));

  // ── Save ─────────────────────────────────────────────────────────
  const save = async () => {
    if (!selectedPart) { toast.error("Pick a Part Number first"); return; }
    setSaving(true);
    // Build totals payload for the backend list view.
    const totals = { manufacturing_cost: computed.manufacturing_cost,
                     profit: computed.profit, profit_pct: computed.profit_pct };
    recipes.forEach(r => { totals[r.key] = computed[r.key]?.cost || 0; });
    const body = {
      part_id: selectedPart.id,
      part_no: selectedPart.part_no,
      part_name: selectedPart.description || selectedPart.company_part_no_description || "",
      customer: selectedPart.customer || "",
      selling_price: num(sellingPrice),
      lines,
      added_processes: addedProcessKeys,
      totals,
      notes,
    };
    try {
      if (isNew) {
        const r = await axios.post(`${API}/cost-sheets`, body);
        toast.success("Cost sheet created");
        navigate(`/cost-sheet/${r.data.id}`, { replace: true });
      } else {
        await axios.put(`${API}/cost-sheets/${id}`, body);
        toast.success("Cost sheet saved");
      }
    } catch (e) { toast.error("Save failed: " + (e.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };

  // ── Print / Export ───────────────────────────────────────────────
  const handlePrint = () => window.print();

  const exportExcel = () => {
    const rows = recipes.map(r => ({
      "Component": r.label,
      "Cost (₹)": Number((computed[r.key]?.cost || 0).toFixed(2)),
      "% of Manufacturing": computed.manufacturing_cost > 0
        ? Number(((computed[r.key]?.cost || 0) / computed.manufacturing_cost * 100).toFixed(2))
        : 0,
    }));
    rows.push({});
    rows.push({ "Component": "Process Cost (subtotal)", "Cost (₹)": Number(computed.process_cost.toFixed(2)) });
    rows.push({ "Component": "ICC (1% of RM)", "Cost (₹)": Number(computed.icc.toFixed(2)) });
    rows.push({ "Component": "Rejection (1% of RM)", "Cost (₹)": Number(computed.rejection.toFixed(2)) });
    rows.push({ "Component": "Overhead (20% of Process Cost)", "Cost (₹)": Number(computed.overhead.toFixed(2)) });
    rows.push({ "Component": "Total Manufacturing Cost", "Cost (₹)": Number(computed.manufacturing_cost.toFixed(2)) });
    rows.push({ "Component": "Selling Price", "Cost (₹)": Number(num(sellingPrice).toFixed(2)) });
    rows.push({ "Component": "Profit", "Cost (₹)": Number(computed.profit.toFixed(2)) });
    rows.push({ "Component": "Profit %", "Cost (₹)": Number(computed.profit_pct.toFixed(2)) });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Cost Sheet");
    XLSX.writeFile(wb, `Cost_Sheet_${selectedPart?.part_no || "new"}.xlsx`);
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    doc.setFillColor(29, 41, 81); doc.rect(0, 0, 210, 22, "F");
    doc.setTextColor(245, 179, 1); doc.setFont("helvetica", "bold"); doc.setFontSize(18);
    doc.text("SWAMI SOFTWARE", 105, 14, { align: "center" });
    doc.setTextColor(255, 255, 255); doc.setFontSize(9);
    doc.text("Cost Sheet", 105, 19, { align: "center" });

    let y = 30;
    doc.setTextColor(15, 23, 42); doc.setFontSize(11); doc.setFont("helvetica", "bold");
    doc.text(`Part No: ${selectedPart?.part_no || "—"}`, 14, y); y += 6;
    doc.setFont("helvetica", "normal");
    doc.text(`Part Name: ${selectedPart?.description || "—"}`, 14, y); y += 5;
    doc.text(`Customer: ${selectedPart?.customer || "—"}`, 14, y); y += 5;
    doc.text(`Selling Price: ${rupee(sellingPrice)}`, 14, y); y += 8;

    const body = recipes.map(r => [
      r.label,
      rupee(computed[r.key]?.cost || 0),
      computed.manufacturing_cost > 0
        ? `${((computed[r.key]?.cost || 0) / computed.manufacturing_cost * 100).toFixed(1)}%`
        : "—",
    ]);
    doc.autoTable({
      startY: y,
      head: [["Cost Component", "Value", "% of Total"]],
      body,
      theme: "grid",
      headStyles: { fillColor: [29, 41, 81], textColor: [255, 255, 255], fontStyle: "bold" },
      styles: { fontSize: 9 },
      columnStyles: { 1: { halign: "right" }, 2: { halign: "right" } },
    });
    y = doc.lastAutoTable.finalY + 8;

    // Overhead / buffer breakdown
    doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(71, 85, 105);
    doc.text(`ICC (1% of RM):`, 18, y); doc.text(rupee(computed.icc), 192, y, { align: "right" }); y += 5;
    doc.text(`Rejection (1% of RM):`, 18, y); doc.text(rupee(computed.rejection), 192, y, { align: "right" }); y += 5;
    doc.text(`Overhead (20% of Process Cost):`, 18, y); doc.text(rupee(computed.overhead), 192, y, { align: "right" }); y += 6;

    doc.setFillColor(29, 41, 81); doc.setTextColor(255, 255, 255);
    doc.rect(14, y - 4, 182, 8, "F");
    doc.setFont("helvetica", "bold"); doc.setFontSize(11);
    doc.text("Total Manufacturing Cost", 18, y + 1);
    doc.text(rupee(computed.manufacturing_cost), 192, y + 1, { align: "right" });
    y += 12;

    const tone = profitTone(computed.profit_pct);
    doc.setFillColor(...(tone.fg === "#166534" ? [220, 252, 231]
                    : tone.fg === "#854D0E" ? [254, 249, 195]
                    : [254, 226, 226]));
    doc.rect(14, y - 4, 182, 20, "F");
    doc.setTextColor(15, 23, 42); doc.setFontSize(10);
    doc.text(`Selling Price: ${rupee(sellingPrice)}`, 18, y + 1);
    doc.text(`Profit / pc: ${rupee(computed.profit)}`, 18, y + 8);
    doc.setFont("helvetica", "bold"); doc.setFontSize(13);
    doc.text(`${computed.profit_pct.toFixed(2)}%`, 192, y + 6, { align: "right" });

    doc.save(`Cost_Sheet_${selectedPart?.part_no || "new"}.pdf`);
  };

  const tone = profitTone(computed.profit_pct);

  return (
    <Layout>
      <style>{`@media print {
        .no-print { display: none !important; }
        body { background: white; }
        /* Print-only mode: hide the process editor sections + add-process
           bar so the printout keeps only the part details header, Cost
           Distribution pie, and Cost Break-up analysis. */
        .print-hide-when-printing { display: none !important; }
      }`}</style>

      <div className="space-y-4 max-w-7xl mx-auto p-4" ref={printRef}>
        {/* ── Toolbar (hidden on print) ── */}
        <div className="no-print flex items-center justify-between flex-wrap gap-2">
          <Button variant="outline" onClick={() => navigate("/cost-sheet")} className="h-9">
            <ArrowLeft size={14} className="mr-1" /> Back
          </Button>
          <div className="flex items-center gap-2">
            {!isNew && (
              <>
                <Button variant="outline" onClick={handlePrint} className="h-9" data-testid="cs-print">
                  <Printer size={14} className="mr-1" /> Print
                </Button>
                <Button variant="outline" onClick={exportExcel} className="h-9 text-[#7C3AED] border-[#7C3AED]" data-testid="cs-xlsx">
                  <FileXls size={14} className="mr-1" /> Excel
                </Button>
                <Button variant="outline" onClick={exportPDF} className="h-9 text-[#DC2626] border-[#DC2626]" data-testid="cs-pdf">
                  <FilePdf size={14} className="mr-1" /> PDF
                </Button>
              </>
            )}
            <Button className="bg-[#1D2951] text-white h-9 gap-1" onClick={save} disabled={saving} data-testid="cs-save">
              <FloppyDisk size={14} /> {saving ? "Saving…" : (isNew ? "Create" : "Save")}
            </Button>
          </div>
        </div>

        {/* ── Part header ── */}
        <Card className="p-5 border border-[#E2E8F0]">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs font-semibold text-[#334155]">Part Number *</Label>
              <div className="mt-1">
                <SearchableSelect
                  value={selectedPart?.id || ""}
                  onChange={(id) => onPickPart(id)}
                  options={parts.map(p => ({
                    value: p.id,
                    label: p.part_no,
                    hint: p.description || p.customer || "",
                  }))}
                  placeholder="Pick a part no…"
                  disabled={!isNew}
                  testId="cs-part-dropdown"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold text-[#334155]">Part Name</Label>
              <Input value={selectedPart?.description || ""} disabled className="mt-1 bg-[#F1F5F9]" data-testid="cs-part-name" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-[#334155]">Customer</Label>
              <Input value={selectedPart?.customer || ""} disabled className="mt-1 bg-[#F1F5F9]" data-testid="cs-customer" />
            </div>
          </div>
        </Card>

        {!selectedPart && (
          <Card className="p-8 text-center border border-dashed border-[#CBD5E1]">
            <p className="text-[#64748B]">Pick a Part Number above — then use <b>+ Add Process</b> to build up your Cost Sheet.</p>
          </Card>
        )}

        {/* ── Recipe sections (only what the user added via + Add Process) ── */}
        <div className="print-hide-when-printing space-y-4">
        {selectedPart && recipes.map((r, idx) => (
          <RecipeSection key={r.key} recipe={r}
            values={lines[r.key] || {}}
            calc={computed[r.key] || {}}
            onChange={patch => setLine(r.key, patch)}
            expanded={!!expanded[r.key]}
            onToggle={() => setExpanded(x => ({ ...x, [r.key]: !x[r.key] }))}
            onRemove={() => removeProcess(r.key)}
            bomMaster={bomMaster}
            cuttingProcess={cuttingProcess}
            rmSection={lines.raw_material || {}}
            rmGrossWeight={computed.raw_material?.gross_weight || 0}
            partSnapshot={selectedPart}
            machiningConfig={machiningConfig}
            processes={processes}
            sequence={idx + 1}
            isDragging={dragKey === r.key}
            draggable={r.key !== "raw_material"}
            onDragStart={() => setDragKey(r.key)}
            onDragOver={(e) => {
              if (!dragKey || dragKey === r.key) return;
              if (r.key === "raw_material") return;
              e.preventDefault();
            }}
            onDrop={() => { reorderProcess(dragKey, r.key); setDragKey(null); }}
            onDragEnd={() => setDragKey(null)}
          />
        ))}

        {/* ── + Add Process picker (always visible while a part is picked) ── */}
        {selectedPart && (
          <AddProcessBar options={addableProcesses} onAdd={addProcess} />
        )}
        </div>

        {/* ── Summary + Profit + Pie chart ── */}
        {selectedPart && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="p-5 border border-[#E2E8F0]">
              <h3 className="text-sm font-bold text-[#1D2951] mb-3">Cost Break-up</h3>
              <div className="space-y-1.5">
                {/* Raw Material — shown as its own line */}
                {(() => {
                  const rmRecipe = recipes.find(r => r.key === "raw_material");
                  if (!rmRecipe) return null;
                  const c = computed.raw_material?.cost || 0;
                  const share = computed.manufacturing_cost > 0 ? c / computed.manufacturing_cost * 100 : 0;
                  return (
                    <div className="flex items-center justify-between text-sm py-1 border-b border-[#F1F5F9]">
                      <span className="text-[#334155]">Raw Material</span>
                      <span className="flex items-center gap-3">
                        <span className="text-[10px] text-[#94A3B8] font-mono w-12 text-right">{share.toFixed(1)}%</span>
                        <span className="font-mono font-semibold text-[#0F172A] w-24 text-right" data-testid="cs-rm-cost">{rupee(c)}</span>
                      </span>
                    </div>
                  );
                })()}
                {/* Process Cost — sum of every non-RM section */}
                <div className="flex items-center justify-between text-sm py-1 border-b border-[#F1F5F9]">
                  <span className="text-[#334155]">
                    Process Cost
                    <span className="text-[10px] text-[#94A3B8] ml-1">(all non-RM processes)</span>
                  </span>
                  <span className="flex items-center gap-3">
                    <span className="text-[10px] text-[#94A3B8] font-mono w-12 text-right">
                      {(computed.manufacturing_cost > 0 ? computed.process_cost / computed.manufacturing_cost * 100 : 0).toFixed(1)}%
                    </span>
                    <span className="font-mono font-semibold text-[#0F172A] w-24 text-right" data-testid="cs-process-cost">{rupee(computed.process_cost)}</span>
                  </span>
                </div>
                {/* Per-process breakdown of the Process Cost, indented */}
                {recipes.filter(r => r.key !== "raw_material").map(r => {
                  const c = computed[r.key]?.cost || 0;
                  if (c <= 0) return null;
                  const share = computed.process_cost > 0 ? c / computed.process_cost * 100 : 0;
                  return (
                    <div key={r.key} className="flex items-center justify-between text-[11px] py-0.5 pl-4 text-[#64748B]" data-testid={`cs-pc-line-${r.key}`}>
                      <span className="flex items-center gap-1.5">
                        <span className="w-1 h-1 rounded-full bg-[#94A3B8]" />
                        {r.label}
                      </span>
                      <span className="flex items-center gap-3">
                        <span className="text-[10px] text-[#94A3B8] font-mono w-12 text-right">{share.toFixed(1)}%</span>
                        <span className="font-mono w-24 text-right">{rupee(c)}</span>
                      </span>
                    </div>
                  );
                })}
                <div className="flex items-center justify-between pt-2 border-t-2 border-[#1D2951]">
                  <span className="font-black text-[#1D2951]">Total Manufacturing Cost</span>
                  <span className="font-mono font-black text-lg text-[#1D2951]">{rupee(computed.manufacturing_cost)}</span>
                </div>
                {/* Overhead buffers — 1% each, always shown so quoters see
                    the "true" landed cost at a glance. */}
                <div className="flex items-center justify-between text-sm pt-2 text-[#475569]">
                  <span>ICC <span className="text-[10px] text-[#94A3B8]">(1% of RM value)</span></span>
                  <span className="font-mono font-semibold" data-testid="cs-icc">{rupee(computed.icc)}</span>
                </div>
                <div className="flex items-center justify-between text-sm text-[#475569]">
                  <span>Rejection <span className="text-[10px] text-[#94A3B8]">(1% of RM value)</span></span>
                  <span className="font-mono font-semibold" data-testid="cs-rejection">{rupee(computed.rejection)}</span>
                </div>
                <div className="flex items-center justify-between text-sm text-[#475569]">
                  <span>Overhead <span className="text-[10px] text-[#94A3B8]">(20% of Process Cost)</span></span>
                  <span className="font-mono font-semibold" data-testid="cs-overhead">{rupee(computed.overhead)}</span>
                </div>
              </div>

              <div className="mt-4 space-y-2 pt-4 border-t border-[#E2E8F0]">
                <div>
                  <Label className="text-xs font-semibold text-[#334155]">Selling Price (₹)</Label>
                  <Input type="number" value={sellingPrice}
                    onChange={e => setSellingPrice(e.target.value)}
                    className="mt-1 h-10 font-mono" data-testid="cs-selling-price" />
                  <p className="text-[10px] text-[#94A3B8] mt-1">Prefilled from Part Rate in Item Master — override if quoting.</p>
                </div>
              </div>

              {/* Profit tile */}
              <div className="mt-3 p-4 rounded" style={{ background: tone.bg, border: `1px solid ${tone.border}` }}>
                <div className="flex items-baseline justify-between">
                  <span className="text-xs font-bold uppercase tracking-wider" style={{ color: tone.fg }}>Profit</span>
                  <span className="text-2xl font-black font-mono" style={{ color: tone.fg }}>{computed.profit_pct.toFixed(2)}%</span>
                </div>
                <div className="mt-1 text-xl font-mono font-bold" style={{ color: tone.fg }}>{rupee(computed.profit)} <span className="text-xs font-normal">/ piece</span></div>
                <div className="text-[11px] mt-1" style={{ color: tone.fg }}>{tone.label}</div>
              </div>
            </Card>

            <Card className="p-5 border border-[#E2E8F0]">
              <h3 className="text-sm font-bold text-[#1D2951] mb-3">Cost Distribution</h3>
              {pieData.length === 0 ? (
                <div className="h-64 flex items-center justify-center text-sm text-[#94A3B8]">
                  Enter costs on any section to see the distribution.
                </div>
              ) : (
                <div className="h-80">
                  <ResponsiveContainer>
                    <PieChart margin={{ top: 24, right: 24, bottom: 8, left: 24 }}>
                      <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%"
                        outerRadius={80} innerRadius={40} paddingAngle={2}
                        label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                        labelLine={{ stroke: "#94A3B8", strokeWidth: 1 }}>
                        {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={(v) => rupee(v)} />
                      <Legend verticalAlign="bottom" height={36} iconSize={10} wrapperStyle={{ fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Card>
          </div>
        )}

        {selectedPart && (
          <Card className="p-4 border border-[#E2E8F0]">
            <Label className="text-xs font-semibold text-[#334155]">Notes</Label>
            <Input value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="Add any quoting notes or assumptions…"
              className="mt-1" data-testid="cs-notes" />
          </Card>
        )}
      </div>
    </Layout>
  );
}


function RMRateSourceNotice({ source }) {
  if (!source) return null;
  if (source.kind === "avg") {
    return (
      <div className="rounded border border-[#BBF7D0] bg-[#F0FDF4] px-3 py-2 text-[11px] text-[#166534] flex items-center gap-2"
        data-testid="cs-rm-rate-avg">
        <span className="w-1.5 h-1.5 rounded-full bg-[#059669]" />
        <span>
          RM Rate auto-picked from weighted-average of <b>{source.samples}</b> accepted receive{source.samples === 1 ? "" : "s"}
          {" "}(<b>{Number(source.qty || 0).toFixed(2)} Kg</b> total)&nbsp;— <b>₹{Number(source.rate || 0).toFixed(2)}/Kg</b>. Feel free to override.
        </span>
      </div>
    );
  }
  return (
    <div className="rounded border border-[#FDE68A] bg-[#FFFBEB] px-3 py-2 text-[11px] text-[#92400E] flex items-center gap-2"
      data-testid="cs-rm-rate-none">
      <span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B]" />
      <span>
        No accepted RM receives found for this code yet — <b>please enter RM Rate manually</b> for now.
      </span>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// Recipe section renderer — knows how to render 4 shapes:
//   • normal recipe (list of inputs → single cost)
//   • machining recipe (CNC + VMC sub-cards)
//   • dynamic-list recipe (surface treatment / surface finish —
//     parent checkbox + rows of treatments)
//   • generic (fallback for unknown operations)
// ─────────────────────────────────────────────────────────────
function RecipeSection({ recipe, values, calc, onChange, expanded, onToggle, onRemove, bomMaster = [], cuttingProcess = null, rmGrossWeight = 0, rmSection = {}, partSnapshot = null, machiningConfig = null, processes = [],
  sequence, isDragging, draggable, onDragStart, onDragOver, onDrop, onDragEnd }) {
  const optional = !!recipe.optional;
  const enabled = optional ? !!values.enabled : true;

  const setInput = (key, val) => onChange({ [key]: val });

  // Business category badge (Process Master → built-in fallback).
  const categoryKey = resolveCategory(recipe.key, processes);
  const catMeta = CATEGORY_META[categoryKey] || {
    label: String(categoryKey || "").replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()),
    tone: CUSTOM_CATEGORY_TONE,
  };

  return (
    <Card
      className={`border overflow-hidden transition-all ${
        isDragging
          ? "border-[#7C3AED] shadow-lg opacity-60 ring-2 ring-[#7C3AED]/30"
          : "border-[#E2E8F0]"
      }`}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      data-testid={`cs-section-${recipe.key}`}
    >
      <div className="px-4 py-3 bg-[#F8FAFC] border-b border-[#E2E8F0] flex items-center justify-between">
        <div className="flex items-center gap-3">
          {/* Drag handle — hidden on Raw Material (pinned at top). Only
              the handle is draggable so inputs inside the card can still
              be text-selected without hijacking the drag. */}
          {draggable ? (
            <span
              draggable
              onDragStart={onDragStart}
              className="cursor-grab active:cursor-grabbing text-[#94A3B8] hover:text-[#1D2951]"
              title="Drag to reorder"
              data-testid={`cs-drag-${recipe.key}`}>
              <DotsSixVertical size={16} weight="bold" />
            </span>
          ) : (
            <span className="w-4" aria-hidden="true" />
          )}
          {sequence && (
            <span className="min-w-[22px] h-[22px] px-1.5 rounded-full bg-[#1D2951] text-white text-[10px] font-black flex items-center justify-center font-mono"
              data-testid={`cs-seq-${recipe.key}`}>
              {sequence}
            </span>
          )}
          <button onClick={onToggle} className="text-[#64748B] hover:text-[#1D2951]" title="Expand / collapse">
            {expanded ? <CaretDown size={16} weight="bold" /> : <CaretRight size={16} weight="bold" />}
          </button>
          {optional && (
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox checked={enabled} onCheckedChange={v => onChange({ enabled: !!v })}
                data-testid={`cs-toggle-${recipe.key}`} />
              <span className="text-sm font-bold text-[#1D2951]">{recipe.label}</span>
            </label>
          )}
          {!optional && <span className="text-sm font-bold text-[#1D2951]">{recipe.label}</span>}
        </div>
        <div className="flex items-center gap-3">
          {/* Top-right category badge — driven by Process Master. */}
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${catMeta.tone}`}
            data-testid={`cs-category-${recipe.key}`}>
            {catMeta.label}
          </span>
          <span className="font-mono font-black text-[#1D2951]">{rupee(enabled ? (calc.cost || 0) : 0)}</span>
          {onRemove && (
            <Button variant="ghost" size="sm" onClick={onRemove}
              className="h-8 w-8 p-0 text-[#94A3B8] hover:text-[#DC2626] hover:bg-[#FEE2E2]"
              title={`Remove ${recipe.label}`}
              data-testid={`cs-remove-${recipe.key}`}>
              <Trash size={14} />
            </Button>
          )}
        </div>
      </div>

      {expanded && enabled && (
        <div className="p-4 space-y-3">
          {/* ── Advanced Raw Material (grade / type / dimensions → GW → net) ── */}
          {recipe.isRawMaterialAdvanced && (
            <RawMaterialAdvanced values={values} calc={calc} onChange={onChange}
              bomMaster={bomMaster} partSnapshot={partSnapshot} />
          )}

          {/* ── Advanced Cutting (laser / water jet / shearing / bandsaw) ── */}
          {recipe.isCuttingAdvanced && (
            <CuttingAdvanced values={values} calc={calc} onChange={onChange}
              cuttingProcess={cuttingProcess} rmGrossWeight={rmGrossWeight}
              rmSection={rmSection} />
          )}

          {/* ── Advanced Machining (CNC or VMC picker + auto MHR) ── */}
          {recipe.isMachiningAdvanced && (
            <MachiningAdvanced values={values} calc={calc} onChange={onChange}
              machiningConfig={machiningConfig} rmSection={rmSection} />
          )}

          {/* ── Advanced Deburring (MHR ÷ (Output × Eff%)) ── */}
          {recipe.isDeburringAdvanced && (
            <DeburringAdvanced values={values} calc={calc} onChange={onChange}
              machiningConfig={machiningConfig} />
          )}

          {/* ── Painting (Area × Rate/sq.ft.) ── */}
          {recipe.isPaintingAdvanced && (
            <PaintingAdvanced values={values} calc={calc} onChange={onChange} />
          )}

          {/* ── Silver Plating (masking + plating with auto rate) ── */}
          {recipe.isSilverPlatingAdvanced && (
            <SilverPlatingAdvanced values={values} calc={calc} onChange={onChange}
              processMeta={processes.find(p => (p.code || "").toLowerCase() === recipe.key.replace(/__\d+$/, ""))} />
          )}

          {/* ── Generic Rate × Weight (Anodizing, Zinc Plating, Packaging, etc.) ── */}
          {recipe.isRateXWeight && (
            <RateXWeight values={values} calc={calc} onChange={onChange}
              processMeta={processes.find(p => (p.code || "").toLowerCase() === recipe.key.replace(/__\d+$/, ""))}
              partSnapshot={partSnapshot} />
          )}

          {/* ── Machining (CNC + VMC) — legacy side-by-side (unused now) ── */}
          {recipe.isMachining && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {recipe.subMachines.map(sm => {
                const subVals = values[sm.key] || {};
                const subCalc = calc[sm.key] || {};
                return (
                  <div key={sm.key} className="border border-[#CBD5E1] rounded p-3 bg-white">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-bold text-[#7C3AED]">{sm.label}</h4>
                      <span className="font-mono font-bold">{rupee(subCalc.cost || 0)} <span className="text-[10px] text-[#94A3B8]">/pc</span></span>
                    </div>
                    <div className="grid grid-cols-1 gap-2">
                      {recipe.machineInputs.map(f => (
                        <div key={f.key}>
                          <Label className="text-[11px] font-semibold text-[#334155]">{f.label}</Label>
                          <Input type={f.type} value={subVals[f.key] ?? ""}
                            placeholder={f.placeholder}
                            onChange={e => onChange({ [sm.key]: { ...subVals, [f.key]: e.target.value } })}
                            className="mt-0.5 h-9 font-mono"
                            data-testid={`cs-${recipe.key}-${sm.key}-${f.key}`} />
                        </div>
                      ))}
                    </div>
                    <div className="mt-2 pt-2 border-t border-[#F1F5F9] text-[11px] text-[#64748B] space-y-0.5">
                      <div className="flex justify-between"><span>Output / hour</span><span className="font-mono">{(subCalc.output_per_hour || 0).toFixed(2)}</span></div>
                      <div className="flex justify-between"><span>Effective Output</span><span className="font-mono">{(subCalc.effective_output || 0).toFixed(2)}</span></div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── Dynamic-list (Surface Treatment / Surface Finish) ── */}
          {recipe.isDynamicList && (
            <DynamicTreatmentList recipe={recipe} values={values} onChange={onChange} />
          )}

          {/* ── Standard input grid ── */}
          {!recipe.isMachining && !recipe.isDynamicList && !recipe.isRawMaterialAdvanced && !recipe.isCuttingAdvanced && !recipe.isMachiningAdvanced && !recipe.isDeburringAdvanced && !recipe.isPaintingAdvanced && !recipe.isSilverPlatingAdvanced && !recipe.isRateXWeight && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {recipe.inputs.map(f => {
                  // Fallback display for autoFrom fields (e.g. Packaging /
                  // Dispatch weight → part.finished_weight). Shown as the
                  // input value when the user hasn't typed one — keeps the
                  // computed cost consistent with what the field shows.
                  const autoVal = f.autoFrom && partSnapshot ? partSnapshot[f.autoFrom] : "";
                  const shownVal = (values[f.key] !== undefined && values[f.key] !== "")
                    ? values[f.key]
                    : (autoVal ?? "");
                  return (
                    <div key={f.key}>
                      <Label className="text-[11px] font-semibold text-[#334155]">
                        {f.label}
                        {f.auto && <span className="ml-1 text-[9px] font-normal text-[#7C3AED]">(auto)</span>}
                        {f.autoFrom && <span className="ml-1 text-[9px] font-normal text-[#7C3AED]">(Part Master)</span>}
                      </Label>
                      <Input type={f.type} value={shownVal}
                        readOnly={f.readOnly}
                        onChange={e => setInput(f.key, e.target.value)}
                        placeholder={f.placeholder}
                        className={`mt-0.5 h-9 font-mono ${f.readOnly ? "bg-[#F1F5F9] cursor-not-allowed" : ""}`}
                        data-testid={`cs-${recipe.key}-${f.key}`} />
                      {f.hint && <p className="text-[9px] text-[#94A3B8] mt-0.5">{f.hint}</p>}
                    </div>
                  );
                })}
              </div>
              {recipe.display && (
                <div className="mt-2 pt-2 border-t border-[#F1F5F9] flex flex-wrap gap-3">
                  {recipe.display(calc, values).map(([lbl, val, tone], i) => (
                    <div key={i} className={`px-3 py-1.5 rounded ${tone === "primary" ? "bg-[#1D2951] text-white" : "bg-[#F1F5F9] text-[#334155]"} text-xs flex items-center gap-2`}>
                      <span className="opacity-80">{lbl}:</span>
                      <span className="font-mono font-bold">{val}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* ── Inspection Cost — manual, added on every process section. ── */}
          <div className="pt-3 mt-2 border-t border-dashed border-[#E2E8F0] grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-[11px] font-semibold text-[#334155]">
                Inspection Cost (₹) <span className="ml-1 text-[9px] font-normal text-[#94A3B8]">— added to this section&apos;s total</span>
              </Label>
              <Input type="number" step="0.01" value={values.inspection_cost ?? ""}
                onChange={e => onChange({ inspection_cost: e.target.value })}
                placeholder="0"
                className="mt-0.5 h-9 font-mono" data-testid={`cs-${recipe.key}-inspection`} />
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}


// ─── Advanced Raw Material section ───────────────────────────────────
function RawMaterialAdvanced({ values, calc, onChange, bomMaster, partSnapshot }) {
  const grades = Array.from(new Set((bomMaster || []).map(b => b.grade).filter(Boolean)));
  const rateOptions = Array.from(new Set(
    (bomMaster || []).map(b => Number(b.total ?? b.rate ?? 0)).filter(x => x > 0)
  )).sort((a, b) => a - b);
  const rmType = values.rm_type || "Rod";
  const rmTypeMeta = RM_TYPES.find(t => t.code === rmType) || RM_TYPES[0];

  const pickGrade = (grade) => {
    const row = (bomMaster || []).find(b => (b.grade || "").toLowerCase() === (grade || "").toLowerCase());
    onChange({
      rm_grade: grade,
      density: row?.density ?? values.density,
      rm_rate: (row?.total ?? row?.rate ?? values.rm_rate),
      scrap_rate: row?.scrap_rate ?? values.scrap_rate,
    });
  };

  // Whenever the user flips RM Type, refresh the dimensional fields from
  // BOM Routing / Part Master. Values remain editable — we only fill
  // fields that are currently blank so we don't stomp on user input.
  const changeRmType = (nextType) => {
    const patch = { rm_type: nextType };
    if (partSnapshot) {
      const t = String(nextType).toLowerCase();
      const auto = (() => {
        if (t === "rod")   return { od: partSnapshot.diameter ?? partSnapshot.outer_diameter ?? "", length: partSnapshot.length ?? "" };
        if (t === "pipe")  return { od: partSnapshot.outer_diameter ?? "", id: partSnapshot.inner_diameter ?? "", length: partSnapshot.length ?? "" };
        if (t === "sheet" || t === "strip") return { width: partSnapshot.width ?? "", length: partSnapshot.length ?? "", thickness: partSnapshot.thickness ?? "" };
        return {};
      })();
      Object.entries(auto).forEach(([k, v]) => {
        if (v !== "" && v != null && (values[k] === "" || values[k] == null)) patch[k] = v;
      });
    }
    onChange(patch);
  };

  // Round helpers only used for the readback labels.
  const fx = (n, d = 3) => Number.isFinite(+n) ? (+n).toFixed(d) : "0";

  return (
    <>
      {values._rm_rate_source && <RMRateSourceNotice source={values._rm_rate_source} />}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <Label className="text-[11px] font-semibold text-[#334155]">RM Grade <span className="ml-1 text-[9px] text-[#7C3AED] font-normal">(BOM Master)</span></Label>
          <div className="mt-0.5">
            <SearchableSelect
              value={values.rm_grade || ""}
              onChange={(g) => pickGrade(g)}
              options={grades.map(g => ({ value: g, label: g }))}
              placeholder="Select grade…"
              size="sm"
              testId="cs-rm-grade"
            />
          </div>
        </div>
        <div>
          <Label className="text-[11px] font-semibold text-[#334155]">RM Type</Label>
          <select value={rmType} onChange={e => changeRmType(e.target.value)}
            className="mt-0.5 h-9 w-full border border-[#E2E8F0] rounded px-2 text-xs bg-white font-mono"
            data-testid="cs-rm-type">
            {RM_TYPES.map(t => <option key={t.code} value={t.code}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-[11px] font-semibold text-[#334155]">Density (Kg/cm³) <span className="ml-1 text-[9px] text-[#7C3AED] font-normal">(auto)</span></Label>
          <Input type="number" step="0.0001" value={values.density ?? ""}
            onChange={e => onChange({ density: e.target.value })}
            className="mt-0.5 h-9 font-mono" data-testid="cs-rm-density" />
        </div>
      </div>

      {/* Dimensional inputs depending on RM Type — auto-filled from BOM Routing */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {rmTypeMeta.inputs.map(f => (
          <div key={f.key}>
            <Label className="text-[11px] font-semibold text-[#334155]">
              {f.label} <span className="ml-1 text-[9px] text-[#7C3AED] font-normal">(BOM Routing)</span>
            </Label>
            <Input type={f.type} value={values[f.key] ?? ""}
              onChange={e => onChange({ [f.key]: e.target.value })}
              className="mt-0.5 h-9 font-mono"
              data-testid={`cs-rm-${f.key}`} />
          </div>
        ))}
      </div>

      {/* Rates block */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <Label className="text-[11px] font-semibold text-[#334155]">RM Rate (₹/Kg) <span className="ml-1 text-[9px] text-[#7C3AED] font-normal">(BOM Master)</span></Label>
          <div className="flex gap-1 mt-0.5">
            <Input type="number" step="0.01" value={values.rm_rate ?? ""}
              onChange={e => onChange({ rm_rate: e.target.value })}
              className="h-9 font-mono" data-testid="cs-rm-rate" />
            {rateOptions.length > 0 && (
              <select onChange={e => e.target.value && onChange({ rm_rate: e.target.value })}
                className="h-9 border border-[#E2E8F0] rounded px-1 text-xs bg-white"
                data-testid="cs-rm-rate-pick">
                <option value="">Pick…</option>
                {rateOptions.map(r => <option key={r} value={r}>₹{r}</option>)}
              </select>
            )}
          </div>
        </div>
        <div>
          <Label className="text-[11px] font-semibold text-[#334155]">Finish Weight (Kg) <span className="ml-1 text-[9px] text-[#7C3AED] font-normal">(Part Master)</span></Label>
          <Input type="number" step="0.001" value={values.finish_weight ?? ""}
            onChange={e => onChange({ finish_weight: e.target.value })}
            className="mt-0.5 h-9 font-mono" data-testid="cs-rm-finish-weight" />
        </div>
        <div>
          <Label className="text-[11px] font-semibold text-[#334155]">Scrap Rate (₹/Kg) <span className="ml-1 text-[9px] text-[#7C3AED] font-normal">(BOM Master)</span></Label>
          <Input type="number" step="0.01" value={values.scrap_rate ?? ""}
            onChange={e => onChange({ scrap_rate: e.target.value })}
            className="mt-0.5 h-9 font-mono" data-testid="cs-rm-scrap-rate" />
        </div>
        <div>
          <Label className="text-[11px] font-semibold text-[#334155]">Scrap Recovery (%)</Label>
          <Input type="number" step="0.01" value={values.scrap_recovery_pct ?? 95}
            onChange={e => onChange({ scrap_recovery_pct: e.target.value })}
            className="mt-0.5 h-9 font-mono" data-testid="cs-rm-scrap-recovery" />
        </div>
      </div>

      {/* Live readouts */}
      <div className="mt-2 pt-2 border-t border-[#F1F5F9] flex flex-wrap gap-3">
        {[
          ["Gross Weight",   `${fx(calc.gross_weight, 4)} Kg`],
          ["Gross RM Value", `₹${fx(calc.gross_rm_value, 2)}`],
          ["Scrap Weight",   `${fx(calc.scrap_weight, 4)} Kg`],
          ["Scrap Value",    `₹${fx(calc.scrap_value, 2)}`],
          ["Net RM Value",   `₹${fx(calc.cost, 2)}`, "primary"],
        ].map(([lbl, val, tone], i) => (
          <div key={i} className={`px-3 py-1.5 rounded ${tone === "primary" ? "bg-[#1D2951] text-white" : "bg-[#F1F5F9] text-[#334155]"} text-xs flex items-center gap-2`}>
            <span className="opacity-80">{lbl}:</span>
            <span className="font-mono font-bold">{val}</span>
          </div>
        ))}
      </div>
    </>
  );
}


// ─── Advanced Cutting section ────────────────────────────────────────
// Default per-type rates if neither the Cost Sheet override nor the
// Process Master value is present. Editable in the UI.
const CUTTING_DEFAULT_RATES = {
  laser: 8,        // ₹ per mm per Thickness
  water_jet: 20,   // ₹ per mm per Thickness
  shearing: 2,     // ₹ per Kg
};

function CuttingAdvanced({ values, calc, onChange, cuttingProcess, rmGrossWeight, rmSection = {} }) {
  const p = cuttingProcess || {};
  const cuttingType = values.cutting_type || p.cutting_type || "";
  const fx = (n, d = 2) => Number.isFinite(+n) ? (+n).toFixed(d) : "0";
  const nn = (v) => { const x = parseFloat(v); return Number.isFinite(x) ? x : 0; };

  const setType = (t) => onChange({ cutting_type: t });

  // Rate resolvers with fallback: user override → Process Master → default.
  const laserRate = values.rate_per_mm_thickness ?? p.cutting_rate_per_mm_thickness ?? CUTTING_DEFAULT_RATES.laser;
  const waterJetRate = values.rate_per_mm_thickness ?? p.cutting_rate_per_mm_thickness ?? CUTTING_DEFAULT_RATES.water_jet;
  const shearingRate = values.rate_per_kg ?? p.cutting_rate_per_kg ?? CUTTING_DEFAULT_RATES.shearing;

  // ── Intelligent Bandsaw — pull from RM section ──────────────────
  const rmType = String(rmSection.rm_type || "").toLowerCase();
  let bandsawAutoArea = null;    // auto-derived sq. in.
  let bandsawAutoSlab = null;    // auto-selected slab key
  let bandsawAutoRate = null;    // auto-selected rate (₹/sq.in.)
  if (cuttingType === "bandsaw") {
    if (rmType === "rod" || rmType === "pipe") {
      const od = nn(rmSection.od);
      if (od > 0) {
        bandsawAutoArea = 0.0013 * od * od;
        bandsawAutoSlab = od <= 100 ? "upto_100" : (od <= 200 ? "100_200" : "above_200");
      }
    } else if (rmType === "sheet" || rmType === "strip") {
      const w = nn(rmSection.width);
      const t = nn(rmSection.thickness);
      if (w > 0 && t > 0) {
        bandsawAutoArea = 0.00155 * w * t;
        // For flat stock the diameter concept doesn't apply — pick the slab
        // based on Width so the auto-slab logic still resolves.
        bandsawAutoSlab = w <= 100 ? "upto_100" : (w <= 200 ? "100_200" : "above_200");
      }
    }
    if (bandsawAutoSlab) {
      // Hard-coded defaults per business rule — Process Master values
      // (if set) override, otherwise fall back to ₹1 / ₹1.5 / ₹2 per sq.in.
      const DEFAULTS = { upto_100: 1, "100_200": 1.5, above_200: 2 };
      bandsawAutoRate = bandsawAutoSlab === "upto_100"
        ? (nn(p.bandsaw_rate_upto_100) || DEFAULTS.upto_100)
        : bandsawAutoSlab === "100_200"
          ? (nn(p.bandsaw_rate_100_200) || DEFAULTS["100_200"])
          : (nn(p.bandsaw_rate_above_200) || DEFAULTS.above_200);
    }
  }

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <Label className="text-[11px] font-semibold text-[#334155]">Type of Cutting</Label>
          <select value={cuttingType} onChange={e => setType(e.target.value)}
            className="mt-0.5 h-9 w-full border border-[#E2E8F0] rounded px-2 text-xs bg-white font-mono"
            data-testid="cs-cutting-type">
            <option value="">— Select cutting type —</option>
            {CUTTING_TYPES_CS.map(t => <option key={t.code} value={t.code}>{t.label}</option>)}
          </select>
        </div>
      </div>

      {(cuttingType === "laser" || cuttingType === "water_jet") && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-[11px] font-semibold text-[#334155]">Rate / mm / Thickness (₹) <span className="ml-1 text-[9px] text-[#7C3AED] font-normal">(Process Master • default ₹{cuttingType === "laser" ? "8" : "20"})</span></Label>
            <Input type="number" step="0.0001"
              value={values.rate_per_mm_thickness ?? (p.cutting_rate_per_mm_thickness ?? (cuttingType === "laser" ? CUTTING_DEFAULT_RATES.laser : CUTTING_DEFAULT_RATES.water_jet))}
              onChange={e => onChange({ rate_per_mm_thickness: e.target.value })}
              className="mt-0.5 h-9 font-mono" data-testid="cs-cutting-rate-mm" />
          </div>
          <div>
            <Label className="text-[11px] font-semibold text-[#334155]">Cutting Path (mm)</Label>
            <Input type="number" step="0.01" value={values.cutting_path ?? ""}
              onChange={e => onChange({ cutting_path: e.target.value })}
              className="mt-0.5 h-9 font-mono" data-testid="cs-cutting-path" />
          </div>
          <div>
            <Label className="text-[11px] font-semibold text-[#334155]">Thickness (mm)</Label>
            <Input type="number" step="0.01" value={values.thickness ?? ""}
              onChange={e => onChange({ thickness: e.target.value })}
              className="mt-0.5 h-9 font-mono" data-testid="cs-cutting-thickness" />
          </div>
        </div>
      )}

      {cuttingType === "shearing" && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-[11px] font-semibold text-[#334155]">Rate per Kg (₹) <span className="ml-1 text-[9px] text-[#7C3AED] font-normal">(Process Master • default ₹2)</span></Label>
            <Input type="number" step="0.01"
              value={values.rate_per_kg ?? (p.cutting_rate_per_kg ?? CUTTING_DEFAULT_RATES.shearing)}
              onChange={e => onChange({ rate_per_kg: e.target.value })}
              className="mt-0.5 h-9 font-mono" data-testid="cs-shearing-rate" />
          </div>
          <div>
            <Label className="text-[11px] font-semibold text-[#334155]">Gross Weight (Kg) <span className="ml-1 text-[9px] text-[#7C3AED] font-normal">(RM Section)</span></Label>
            <Input type="number" step="0.001"
              value={values.gross_weight ?? rmGrossWeight}
              onChange={e => onChange({ gross_weight: e.target.value })}
              className="mt-0.5 h-9 font-mono" data-testid="cs-shearing-gw" />
          </div>
        </div>
      )}

      {cuttingType === "bandsaw" && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-[11px] font-semibold text-[#334155]">
              Applicable Range <span className="ml-1 text-[9px] text-[#7C3AED] font-normal">(auto)</span>
            </Label>
            <Input readOnly
              value={bandsawAutoSlab === "upto_100" ? "Up to 100 mm"
                : bandsawAutoSlab === "100_200" ? "100 – 200 mm"
                : bandsawAutoSlab === "above_200" ? "Above 200 mm"
                : "—"}
              className="mt-0.5 h-9 font-mono bg-[#F1F5F9]" data-testid="cs-bandsaw-range" />
          </div>
          <div>
            <Label className="text-[11px] font-semibold text-[#334155]">
              Rate (₹/sq.in.) <span className="ml-1 text-[9px] text-[#7C3AED] font-normal">(auto)</span>
            </Label>
            <Input readOnly
              value={bandsawAutoRate != null ? "₹" + fx(bandsawAutoRate, 2) : "—"}
              className="mt-0.5 h-9 font-mono bg-[#F1F5F9]" data-testid="cs-bandsaw-rate" />
          </div>
          <div>
            <Label className="text-[11px] font-semibold text-[#334155]">
              Total Cutting Area (sq.in.) <span className="ml-1 text-[9px] text-[#7C3AED] font-normal">(auto)</span>
            </Label>
            <Input readOnly
              value={bandsawAutoArea != null ? bandsawAutoArea.toFixed(4) : "—"}
              className="mt-0.5 h-9 font-mono bg-[#F1F5F9]" data-testid="cs-bandsaw-area" />
          </div>
          <div>
            <Label className="text-[11px] font-semibold text-[#334155]">
              Cutting Cost <span className="ml-1 text-[9px] text-[#7C3AED] font-normal">(auto)</span>
            </Label>
            <Input readOnly
              value={"₹" + fx((bandsawAutoRate || 0) * (bandsawAutoArea || 0), 2)}
              className="mt-0.5 h-9 font-mono bg-[#F1F5F9] font-bold" data-testid="cs-bandsaw-cost" />
          </div>
        </div>
      )}

      {cuttingType !== "bandsaw" && (
        <div className="mt-2 pt-2 border-t border-[#F1F5F9] flex flex-wrap gap-3">
          <div className="px-3 py-1.5 rounded bg-[#1D2951] text-white text-xs flex items-center gap-2">
            <span className="opacity-80">Cutting Cost:</span>
            <span className="font-mono font-bold">₹{fx(calc.cost, 2)}</span>
          </div>
        </div>
      )}
    </>
  );
}


// ─── Advanced Machining (CNC + VMC side by side; totals combined) ────
function MachiningColumn({ title, subtitle, enabled, onToggle, children, cost, mhr, size, output, testPrefix, slabLabel }) {
  const fx = (n, d = 2) => Number.isFinite(+n) ? (+n).toFixed(d) : "0";
  return (
    <div className={`rounded-lg border ${enabled ? "border-[#CBD5E1]" : "border-dashed border-[#CBD5E1] opacity-60"} bg-white p-3`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-7 h-7 rounded bg-[#1D2951] text-[#D4A843] text-[10px] font-black tracking-wider">
            {title}
          </span>
          <span className="text-xs font-bold text-[#1D2951]">{subtitle}</span>
        </div>
        <label className="flex items-center gap-1.5 text-[10px] text-[#64748B]">
          <input type="checkbox" checked={!!enabled} onChange={(e) => onToggle(e.target.checked)}
            data-testid={`cs-${testPrefix}-enabled`} />
          Include
        </label>
      </div>
      <div className={`${enabled ? "" : "pointer-events-none"} space-y-2`}>
        {children}
        <div className="mt-1 pt-2 border-t border-[#F1F5F9] text-[10px] text-[#64748B]">
          Standard MHR: {slabLabel || "—"}
        </div>
        <div className="flex flex-wrap gap-2 text-[11px]">
          <span className="px-2 py-1 rounded bg-[#F1F5F9] text-[#334155]">Size: <b className="font-mono">{fx(size, 2)} mm</b></span>
          <span className="px-2 py-1 rounded bg-[#F1F5F9] text-[#334155]">MHR: <b className="font-mono">₹{fx(mhr, 0)}/hr</b></span>
          <span className="px-2 py-1 rounded bg-[#F1F5F9] text-[#334155]">Out/hr: <b className="font-mono">{fx(output, 3)}</b></span>
          <span className="px-2 py-1 rounded bg-[#1D2951] text-white ml-auto">Cost: <b className="font-mono">₹{fx(cost, 2)}</b></span>
        </div>
      </div>
    </div>
  );
}

function MachiningAdvanced({ values, calc, onChange, machiningConfig, rmSection }) {
  const cfg = machiningConfig || { cnc_slabs: [], vmc_slabs: [] };
  const rm  = rmSection || {};
  const fx = (n, d = 2) => Number.isFinite(+n) ? (+n).toFixed(d) : "0";
  const slabLabel = (slabs) => {
    const sorted = (slabs || []).slice().sort((a, b) => a.max_size - b.max_size);
    return sorted.map((s, i) => {
      const prev = i === 0 ? 0 : sorted[i - 1].max_size;
      const range = i === 0 ? `Up to ${s.max_size} mm` : `${prev}–${s.max_size} mm`;
      return `${range} → ₹${s.rate}/hr`;
    }).join(" • ");
  };
  const cncEnabled = values.cnc_enabled ?? true;
  const vmcEnabled = values.vmc_enabled ?? true;
  const vmcMode = values.vmc_size_mode || "diameter";
  const cnc = calc.cnc || { size: 0, mhr: 0, output_per_hour: 0, cost: 0 };
  const vmc = calc.vmc || { size: 0, mhr: 0, output_per_hour: 0, cost: 0 };

  // Auto-fill previews — mirror the compute() logic in costRecipes.js so
  // the user sees what's about to be used when the field is left blank.
  const rmType = String(rm.rm_type || "").toLowerCase();
  const isRodLike = rmType === "rod" || rmType === "pipe";
  const isSheetLike = rmType === "sheet";
  const rmOd = Number(rm.od || 0);
  const rmW  = Number(rm.width || 0);
  const rmL  = Number(rm.length || 0);
  const autoSingle = isRodLike ? rmOd : (isSheetLike ? Math.max(rmW, rmL) : 0);
  const autoSmall  = isSheetLike ? Math.min(rmW, rmL) : 0;
  const autoBig    = isSheetLike ? Math.max(rmW, rmL) : 0;

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* ── CNC column ── */}
        <MachiningColumn
          title="CNC" subtitle="CNC Machining"
          enabled={cncEnabled}
          onToggle={(v) => onChange({ cnc_enabled: v })}
          size={cnc.size} mhr={cnc.mhr} output={cnc.output_per_hour} cost={cnc.cost}
          testPrefix="cnc"
          slabLabel={slabLabel(cfg.cnc_slabs)}
        >
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-[10px] font-semibold text-[#334155]">
                OD (mm)
                {autoSingle > 0 && (
                  <span className="ml-1 text-[9px] text-[#7C3AED] font-normal">
                    (RM: {autoSingle}{isSheetLike ? " · max(W,L)" : ""})
                  </span>
                )}
              </Label>
              <Input type="number" step="0.01" value={values.cnc_od ?? ""}
                placeholder={autoSingle > 0 ? String(autoSingle) : ""}
                onChange={e => onChange({ cnc_od: e.target.value })}
                className="mt-0.5 h-9 font-mono text-xs" data-testid="cs-cnc-od" />
            </div>
            <div>
              <Label className="text-[10px] font-semibold text-[#334155]">Cycle (min)</Label>
              <Input type="number" step="0.01" value={values.cnc_cycle_time ?? ""}
                onChange={e => onChange({ cnc_cycle_time: e.target.value })}
                className="mt-0.5 h-9 font-mono text-xs" data-testid="cs-cnc-cycle" />
            </div>
            <div>
              <Label className="text-[10px] font-semibold text-[#334155]">Eff (%)</Label>
              <Input type="number" step="0.01" value={values.cnc_efficiency ?? 75}
                onChange={e => onChange({ cnc_efficiency: e.target.value })}
                className="mt-0.5 h-9 font-mono text-xs" data-testid="cs-cnc-eff" />
            </div>
          </div>
        </MachiningColumn>

        {/* ── VMC column ── */}
        <MachiningColumn
          title="VMC" subtitle="VMC Machining"
          enabled={vmcEnabled}
          onToggle={(v) => onChange({ vmc_enabled: v })}
          size={vmc.size} mhr={vmc.mhr} output={vmc.output_per_hour} cost={vmc.cost}
          testPrefix="vmc"
          slabLabel={slabLabel(cfg.vmc_slabs)}
        >
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] font-semibold text-[#334155]">Size Mode</Label>
              <select value={vmcMode}
                onChange={e => onChange({ vmc_size_mode: e.target.value })}
                className="mt-0.5 h-9 w-full border border-[#E2E8F0] rounded px-2 text-xs bg-white font-mono"
                data-testid="cs-vmc-mode">
                <option value="diameter">Diameter</option>
                <option value="wl">Width × Length</option>
              </select>
            </div>
            {vmcMode === "diameter" ? (
              <div>
                <Label className="text-[10px] font-semibold text-[#334155]">
                  Diameter (mm)
                  {autoSingle > 0 && (
                    <span className="ml-1 text-[9px] text-[#7C3AED] font-normal">
                      (RM: {autoSingle}{isSheetLike ? " · max(W,L)" : ""})
                    </span>
                  )}
                </Label>
                <Input type="number" step="0.01" value={values.vmc_diameter ?? ""}
                  placeholder={autoSingle > 0 ? String(autoSingle) : ""}
                  onChange={e => onChange({ vmc_diameter: e.target.value })}
                  className="mt-0.5 h-9 font-mono text-xs" data-testid="cs-vmc-dia" />
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2 col-span-1">
                <div>
                  <Label className="text-[10px] font-semibold text-[#334155]">
                    W (mm)
                    {autoSmall > 0 && (
                      <span className="ml-1 text-[9px] text-[#7C3AED] font-normal">(RM: {autoSmall})</span>
                    )}
                  </Label>
                  <Input type="number" step="0.01" value={values.vmc_width ?? ""}
                    placeholder={autoSmall > 0 ? String(autoSmall) : ""}
                    onChange={e => onChange({ vmc_width: e.target.value })}
                    className="mt-0.5 h-9 font-mono text-xs" data-testid="cs-vmc-width" />
                </div>
                <div>
                  <Label className="text-[10px] font-semibold text-[#334155]">
                    L (mm)
                    {autoBig > 0 && (
                      <span className="ml-1 text-[9px] text-[#7C3AED] font-normal">(RM: {autoBig})</span>
                    )}
                  </Label>
                  <Input type="number" step="0.01" value={values.vmc_length ?? ""}
                    placeholder={autoBig > 0 ? String(autoBig) : ""}
                    onChange={e => onChange({ vmc_length: e.target.value })}
                    className="mt-0.5 h-9 font-mono text-xs" data-testid="cs-vmc-length" />
                </div>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] font-semibold text-[#334155]">Cycle (min)</Label>
              <Input type="number" step="0.01" value={values.vmc_cycle_time ?? ""}
                onChange={e => onChange({ vmc_cycle_time: e.target.value })}
                className="mt-0.5 h-9 font-mono text-xs" data-testid="cs-vmc-cycle" />
            </div>
            <div>
              <Label className="text-[10px] font-semibold text-[#334155]">Eff (%)</Label>
              <Input type="number" step="0.01" value={values.vmc_efficiency ?? 75}
                onChange={e => onChange({ vmc_efficiency: e.target.value })}
                className="mt-0.5 h-9 font-mono text-xs" data-testid="cs-vmc-eff" />
            </div>
          </div>
        </MachiningColumn>
      </div>

      {/* Total Machining Cost */}
      <div className="mt-3 flex items-center justify-between rounded-lg bg-gradient-to-r from-[#1D2951] to-[#243766] px-4 py-3 text-white shadow-md">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-[#D4A843] font-bold">Total Machining Cost</p>
          <p className="text-[10px] text-slate-300">CNC + VMC combined</p>
        </div>
        <span className="font-mono font-black text-xl" data-testid="cs-machining-total">
          ₹{fx(calc.cost, 2)}
        </span>
      </div>
    </>
  );
}


// ─── Advanced Deburring (MHR ÷ (Output × Eff%)) ─────────────────────
function DeburringAdvanced({ values, calc, onChange, machiningConfig }) {
  const defaultMhr = machiningConfig?.deburring_mhr ?? 180;
  const DEFAULT_EFF = 75;
  const fx = (n, d = 2) => Number.isFinite(+n) ? (+n).toFixed(d) : "0";
  // Show the default (180) whenever the user hasn't typed a value.
  const mhrShown = (values.mhr !== undefined && values.mhr !== "") ? values.mhr : defaultMhr;
  // Efficiency defaults to 75 unless the user typed something.
  const effShown = (values.efficiency !== undefined && values.efficiency !== "") ? values.efficiency : DEFAULT_EFF;
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <Label className="text-[11px] font-semibold text-[#334155]">
            Machine Hour Rate (₹/Hr) <span className="ml-1 text-[9px] text-[#7C3AED] font-normal">(default ₹{defaultMhr})</span>
          </Label>
          <Input type="number" step="0.01" value={mhrShown}
            onChange={e => onChange({ mhr: e.target.value })}
            className="mt-0.5 h-9 font-mono" data-testid="cs-deburring-mhr" />
        </div>
        <div>
          <Label className="text-[11px] font-semibold text-[#334155]">Output per Hour</Label>
          <Input type="number" step="0.01" value={values.output_per_hour ?? ""}
            onChange={e => onChange({ output_per_hour: e.target.value })}
            className="mt-0.5 h-9 font-mono" data-testid="cs-deburring-output" />
        </div>
        <div>
          <Label className="text-[11px] font-semibold text-[#334155]">
            Efficiency (%) <span className="ml-1 text-[9px] text-[#7C3AED] font-normal">(default {DEFAULT_EFF}%)</span>
          </Label>
          <Input type="number" step="0.01" value={effShown}
            onChange={e => onChange({ efficiency: e.target.value })}
            className="mt-0.5 h-9 font-mono" data-testid="cs-deburring-eff" />
        </div>
      </div>
      <div className="mt-2 pt-2 border-t border-[#F1F5F9] flex flex-wrap gap-3">
        <div className="px-3 py-1.5 rounded bg-[#1D2951] text-white text-xs flex items-center gap-2">
          <span className="opacity-80">Cost / piece:</span>
          <span className="font-mono font-bold">₹{fx(calc.cost, 2)}</span>
        </div>
      </div>
    </>
  );
}


// ─── Painting (Area × Rate/sq.ft) ────────────────────────────────────
function PaintingAdvanced({ values, calc, onChange }) {
  const fx = (n, d = 2) => Number.isFinite(+n) ? (+n).toFixed(d) : "0";
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <Label className="text-[11px] font-semibold text-[#334155]">Area (sq. ft.)</Label>
          <Input type="number" step="0.01" value={values.area_sq_ft ?? ""}
            onChange={e => onChange({ area_sq_ft: e.target.value })}
            className="mt-0.5 h-9 font-mono" data-testid="cs-painting-area" />
        </div>
        <div>
          <Label className="text-[11px] font-semibold text-[#334155]">Rate per sq. ft. (₹)</Label>
          <Input type="number" step="0.01" value={values.rate_per_sq_ft ?? ""}
            onChange={e => onChange({ rate_per_sq_ft: e.target.value })}
            className="mt-0.5 h-9 font-mono" data-testid="cs-painting-rate" />
        </div>
        <div>
          <Label className="text-[11px] font-semibold text-[#334155]">
            Painting Cost <span className="ml-1 text-[9px] text-[#7C3AED] font-normal">(auto)</span>
          </Label>
          <Input readOnly value={"₹" + fx((calc.cost || 0) - (calc.inspection_cost || 0), 2)}
            className="mt-0.5 h-9 font-mono bg-[#F1F5F9] font-bold" data-testid="cs-painting-cost" />
        </div>
      </div>
    </>
  );
}


// ─── Silver Plating (Masking + Plating) ──────────────────────────────
function SilverPlatingAdvanced({ values, calc, onChange, processMeta }) {
  const fx = (n, d = 2) => Number.isFinite(+n) ? (+n).toFixed(d) : "0";
  const stdRate = processMeta?.standardised_rate;
  return (
    <>
      {/* A. Masking */}
      <div className="text-[11px] font-bold text-[#0F172A] mt-1">A. Masking</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <Label className="text-[11px] font-semibold text-[#334155]">Masking Area (cm²)</Label>
          <Input type="number" step="0.01" value={values.masking_area ?? ""}
            onChange={e => onChange({ masking_area: e.target.value })}
            className="mt-0.5 h-9 font-mono" data-testid="cs-sp-masking-area" />
        </div>
        <div>
          <Label className="text-[11px] font-semibold text-[#334155]">Masking Rate (₹/cm²)</Label>
          <Input type="number" step="0.01" value={values.masking_rate ?? ""}
            onChange={e => onChange({ masking_rate: e.target.value })}
            className="mt-0.5 h-9 font-mono" data-testid="cs-sp-masking-rate" />
        </div>
        <div>
          <Label className="text-[11px] font-semibold text-[#334155]">Masking Cost <span className="ml-1 text-[9px] text-[#7C3AED] font-normal">(auto)</span></Label>
          <Input readOnly value={"₹" + fx(calc.masking_cost, 2)}
            className="mt-0.5 h-9 font-mono bg-[#F1F5F9] font-bold" data-testid="cs-sp-masking-cost" />
        </div>
      </div>

      {/* B. Plating */}
      <div className="text-[11px] font-bold text-[#0F172A] mt-1">B. Silver Plating</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <Label className="text-[11px] font-semibold text-[#334155]">Plating Area (cm²)</Label>
          <Input type="number" step="0.01" value={values.plating_area ?? ""}
            onChange={e => onChange({ plating_area: e.target.value })}
            className="mt-0.5 h-9 font-mono" data-testid="cs-sp-plating-area" />
        </div>
        <div>
          <Label className="text-[11px] font-semibold text-[#334155]">Thickness (Micron)</Label>
          <Input type="number" step="0.01" value={values.plating_thickness ?? ""}
            onChange={e => onChange({ plating_thickness: e.target.value })}
            className="mt-0.5 h-9 font-mono" data-testid="cs-sp-thickness" />
        </div>
        <div>
          <Label className="text-[11px] font-semibold text-[#334155]">
            Silver Plating Rate <span className="ml-1 text-[9px] text-[#7C3AED] font-normal">(Process Master)</span>
          </Label>
          <Input type="number" step="0.0001"
            value={values.plating_rate ?? (stdRate ?? "")}
            onChange={e => onChange({ plating_rate: e.target.value })}
            className="mt-0.5 h-9 font-mono" data-testid="cs-sp-plating-rate" />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <Label className="text-[11px] font-semibold text-[#334155]">
            Plating Cost <span className="ml-1 text-[9px] text-[#7C3AED] font-normal">(auto)</span>
          </Label>
          <Input readOnly value={"₹" + fx(calc.plating_cost, 2)}
            className="mt-0.5 h-9 font-mono bg-[#F1F5F9] font-bold" data-testid="cs-sp-plating-cost" />
        </div>
        <div>
          <Label className="text-[11px] font-semibold text-[#334155]">
            Consumption (grams) <span className="ml-1 text-[9px] text-[#7C3AED] font-normal">(area × thickness × 0.001049)</span>
          </Label>
          <Input readOnly value={fx(calc.consumption_g, 4)}
            className="mt-0.5 h-9 font-mono bg-[#F1F5F9]" data-testid="cs-sp-consumption-g" />
        </div>
      </div>
    </>
  );
}


// ─── Generic Rate × Weight (surface treatment / finish / heat / packaging / dispatch) ──
function RateXWeight({ values, calc, onChange, processMeta, partSnapshot }) {
  const stdRate = processMeta?.standardised_rate;
  const fx = (n, d = 2) => Number.isFinite(+n) ? (+n).toFixed(d) : "0";
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      <div>
        <Label className="text-[11px] font-semibold text-[#334155]">
          Rate per Kg (₹) <span className="ml-1 text-[9px] text-[#7C3AED] font-normal">(Process Master)</span>
        </Label>
        <Input type="number" step="0.01"
          value={values.rate_per_kg ?? (stdRate ?? "")}
          onChange={e => onChange({ rate_per_kg: e.target.value })}
          className="mt-0.5 h-9 font-mono" data-testid="cs-rxw-rate" />
      </div>
      <div>
        <Label className="text-[11px] font-semibold text-[#334155]">Weight (Kg)</Label>
        <Input type="number" step="0.001"
          value={values.weight ?? (partSnapshot?.finished_weight ?? "")}
          onChange={e => onChange({ weight: e.target.value })}
          className="mt-0.5 h-9 font-mono" data-testid="cs-rxw-weight" />
      </div>
      <div>
        <Label className="text-[11px] font-semibold text-[#334155]">
          Cost <span className="ml-1 text-[9px] text-[#7C3AED] font-normal">(auto)</span>
        </Label>
        <Input readOnly value={"₹" + fx((calc.cost || 0) - (calc.inspection_cost || 0), 2)}
          className="mt-0.5 h-9 font-mono bg-[#F1F5F9] font-bold" data-testid="cs-rxw-cost" />
      </div>
    </div>
  );
}


// ─── + Add Process bar ──────────────────────────────────────────────
function AddProcessBar({ options, onAdd }) {
  const [pick, setPick] = useState("");
  const submit = () => {
    if (!pick) return;
    onAdd(pick);
    setPick("");
  };
  return (
    <div className="flex items-center gap-2 flex-wrap py-2" data-testid="cs-add-process-bar">
      <div className="min-w-[240px]">
        <SearchableSelect
          value={pick}
          onChange={(v) => setPick(v)}
          options={options.map(o => ({ value: o.key, label: o.label }))}
          placeholder="Choose a process to add…"
          size="sm"
          testId="cs-add-process-select"
        />
      </div>
      <Button onClick={submit} className="h-9 bg-[#1D2951] text-white gap-1" disabled={!pick}
        data-testid="cs-add-process-btn">
        <Plus size={14} /> Add Process
      </Button>
      {options.length === 0 && (
        <span className="text-xs text-[#94A3B8]">Every process is already on the sheet.</span>
      )}
    </div>
  );
}


function DynamicTreatmentList({ recipe, values, onChange }) {
  const list = values.treatments || [];
  const setList = (next) => onChange({ treatments: next });

  const add = (name = "") => setList([...list, { name, ...(recipe.treatmentInputs.reduce((a, f) => ({ ...a, [f.key]: "" }), {})) }]);
  const remove = (i) => setList(list.filter((_, x) => x !== i));
  const update = (i, patch) => setList(list.map((t, x) => x === i ? { ...t, ...patch } : t));

  return (
    <div className="space-y-2">
      {list.length === 0 && (
        <p className="text-xs text-[#94A3B8] text-center py-2">No {recipe.label.toLowerCase()} added — pick one below to get started.</p>
      )}
      {list.map((t, i) => (
        <div key={i} className="border border-[#CBD5E1] rounded p-3 bg-white">
          <div className="flex items-center gap-2 mb-2">
            <Input value={t.name || ""} onChange={e => update(i, { name: e.target.value })}
              placeholder="Treatment name (e.g. Hardening)"
              className="h-9 flex-1 font-semibold"
              data-testid={`cs-${recipe.key}-${i}-name`} />
            <Button variant="ghost" size="sm" className="h-9 w-9 p-0 text-[#DC2626]"
              onClick={() => remove(i)}
              data-testid={`cs-${recipe.key}-${i}-remove`}>
              <Trash size={14} />
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {recipe.treatmentInputs.map(f => (
              <div key={f.key}>
                <Label className="text-[10px] font-semibold text-[#334155]">{f.label}</Label>
                <Input type={f.type} value={t[f.key] ?? ""}
                  onChange={e => update(i, { [f.key]: e.target.value })}
                  className="mt-0.5 h-8 font-mono"
                  data-testid={`cs-${recipe.key}-${i}-${f.key}`} />
              </div>
            ))}
            <div className="col-span-2 text-right text-xs">
              Cost: <span className="font-mono font-bold text-[#1D2951]">{rupee(num(t.rate_per_kg) * num(t.weight))}</span>
            </div>
          </div>
        </div>
      ))}
      <div className="flex items-center gap-2 pt-1 flex-wrap">
        {recipe.presetTreatments?.map(name => (
          <Button key={name} variant="outline" size="sm" onClick={() => add(name)}
            className="h-8 text-xs" data-testid={`cs-${recipe.key}-preset-${name}`}>
            <Plus size={12} className="mr-1" /> {name}
          </Button>
        ))}
        <Button size="sm" onClick={() => add("")}
          className="h-8 text-xs bg-[#1D2951] text-white"
          data-testid={`cs-${recipe.key}-add-custom`}>
          <Plus size={12} className="mr-1" /> Custom
        </Button>
      </div>
    </div>
  );
}
